/**
 * `/reports` — ask for a report, see what came of it, take the file.
 *
 * **The screen is deliberately two panels rather than one button.** A report in
 * Wend is a *definition* plus a *run*: the definition says what to read and over
 * which period, and the run says when it happened, what started it, what came
 * out and whether it failed. The shape this rejects is a button that streams a
 * spreadsheet built inline from whatever is in the form — which works, and which
 * cannot be scheduled later without being rewritten, because when a schedule
 * fires there is no request, no signed-in user and nowhere to put the file. So
 * the form starts a run and the list is the record; the same generator is
 * reached from both, and this screen is only one of its two front doors.
 *
 * **The rule slice 10 wrote into the Overview's header governs this page too:
 * what a screen may claim.** Applied here it comes to four sentences that are
 * not interchangeable:
 *
 *   - A run in progress is not a failed run. It is queued, or it is building.
 *   - A failed run is visible **as a failed run**, with its reason — never as a
 *     missing file, never as a blank row. "No report" and "the report failed"
 *     are different statements, and only one of them means somebody has to do
 *     something. This is the same zero-versus-absent rule the rest of the
 *     product lives by, applied to a job instead of to a metric.
 *   - Every run states the **period it covered**, not only when it ran.
 *     "Generated 3 March" tells nobody what is in the file.
 *   - A period in which nothing moved is a **finished report of a quiet
 *     period**, not a failure and not an error — the workbook says so on its own
 *     sheets, and there is deliberately no `no_data` failure code for it.
 *
 * **The hole this screen leaves, stated rather than hidden.** A run record
 * carries no row count, so this app cannot say whether a *particular* finished
 * report turned out to be empty — only the sheet inside can. So the empty case
 * is explained where a person is choosing a period, in the product's own words
 * and quoting the sentence the sheet actually prints, and no row here ever
 * claims a specific file is empty. Inventing that claim from a small `byteSize`
 * would be exactly the guess this codebase keeps catching.
 *
 * **Nothing here is a report designer, and that is the scope slice 13 set.**
 * There is one format, so it is not a control. Slice 44 added the second kind,
 * so the kind is one — a picker, with what its workbook contains stated in
 * words under it.
 *
 * **Slice 17 cut the intro to what a reader would be wrong without.** The
 * paragraph that stood here explained that a report is a definition plus a run
 * and that the same generator serves a schedule — true, load-bearing for whoever
 * builds the schedule screen, and of no use whatever to somebody who came to
 * fetch a spreadsheet. That argument is the first paragraph of this header and
 * is no longer on the screen. What stayed is the pair of facts a person acts on:
 * a run starts now and appears below before its file exists, and nothing on this
 * page can change anything — a report is a read. (It said "a read over this
 * workspace's credit history" until slice 44's Results by surface, which reads
 * campaigns, delivery events and the ad account, made that false.)
 */
import { useState } from 'react';

import { AppLayout } from '../../components/AppLayout';
import { ReportRunsPanel } from './ReportRunsPanel';
import { RequestReportPanel } from './RequestReportPanel';

export function ReportsPage() {
  // Bumped when a run is started, which is the list's cue to re-read. The list
  // owns the runs; nothing here keeps a second copy of a record that would then
  // have to be kept in step with the one the API has.
  const [startedRuns, setStartedRuns] = useState(0);

  return (
    <AppLayout title="Reports">
      <div className="flex flex-col gap-6">
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          Asking for a report starts a run. It appears below before its file exists, and stays there
          saying what it covered and how it ended. A report is a read and changes nothing.
        </p>

        <RequestReportPanel
          onRunStarted={() => {
            setStartedRuns((value) => value + 1);
          }}
        />

        <ReportRunsPanel refreshKey={startedRuns} />
      </div>
    </AppLayout>
  );
}
